/**
 * Rosewear — "Add your branding" post-cart wizard.
 * Pairs with snippets/branding-wizard.liquid. See IMPLEMENTATION-GUIDE.md.
 *
 * Public API (call this from your existing add-to-cart success handler):
 *
 *   RosewearBranding.open({
 *     cartItemKey: addedItem.key,          // string, from the cart/add.js response
 *     productTitle: addedItem.product_title,
 *     variantTitle: addedItem.variant_title,
 *     quantity: addedItem.quantity,
 *     imageUrl: addedItem.image,
 *     mode: 'included'                     // 'included' after a normal add-to-cart,
 *   });                                    // 'extra' from the "Add another logo/text" button
 */
(function () {
  'use strict';

  // ---- EDITOR TODO: fill these in (see IMPLEMENTATION-GUIDE.md step 1) ----
  const CLOUDINARY_CLOUD_NAME = 'ctqdjy7j';
  const CLOUDINARY_UPLOAD_PRESET = 'Rosewear Logos';
  // Real Shopify variant for the chargeable "extra branding" add-on (already created, Draft product):
  const EXTRA_BRANDING_VARIANT_ID = 64246844588409;
  const EXTRA_BRANDING_PRICE_LABEL = '£4.50'; // display only — Shopify enforces the real price
  // Photos shown on the Method step to illustrate Embroidery vs Print. These are set by
  // snippets/branding-wizard.liquid (via asset_url, so cache-busting/CDN fingerprinting stays
  // correct) — fall back to blank (plain colour placeholder) if that snippet hasn't rendered yet.
  const EMBROIDERY_SAMPLE_IMAGE_URL = window.RWB_EMBROIDERY_SAMPLE_URL || '';
  const PRINT_SAMPLE_IMAGE_URL = window.RWB_PRINT_SAMPLE_URL || '';
  // ---------------------------------------------------------------------

  const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Real AI-generated product-stencil photos (per Wyan's call — hand-drawn SVG was rejected
  // three times as "too basic" / not premium enough) replaced the old single hand-drawn
  // silhouette. Each garment CATEGORY now has its own front/back/left/right stencil image,
  // hosted on Shopify's CDN (uploaded via fileCreate from the AI generations). Sleeveless
  // categories (gilet, hivis) only have front/back — Left/Right Sleeve positions are hidden
  // for those (see `sleeveless` below and the `sleeved` flag on positions).
  //
  // Hi-Vis intentionally reuses the Gilet images — same sleeveless-vest construction, so a
  // separate generation wasn't needed (Wyan's call).
  const GARMENT_IMAGE_SETS = {
    gilet: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_223400_f9231ba7-5f2b-4cd2-8b14-853c36e7cbc0.png?v=1787007813',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225854_0612202a-8040-4e3f-a541-be2de96e7125.png?v=1787007812',
      sleeveless: true,
      frontZip: true,
    },
    hivis: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_223400_f9231ba7-5f2b-4cd2-8b14-853c36e7cbc0.png?v=1787007813',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225854_0612202a-8040-4e3f-a541-be2de96e7125.png?v=1787007812',
      sleeveless: true,
      frontZip: true,
    },
    // Softshell, Fleece, and no-hood Waterproof deliberately share ONE image set (Wyan's
    // call, 2026-08-18) — same plain ribbed-cuff zip jacket construction, so there's no
    // need for three separate generations. `side` is a single profile photo mirrored via
    // CSS/SVG transform for Left Sleeve vs Right Sleeve (see garmentSvg()) instead of
    // needing distinct left/right photos — cuts the asset count for these three in half.
    softshell: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_539c5217-5e21-4ba8-b084-c270b53b3e99.png?v=1787011259',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_a0dd44b5-0e6a-4bb9-9d77-bf1f82bd72ea.png?v=1787011259',
      side: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_2f22f8f8-c12f-4302-972f-6730d5dc093c.png?v=1787011259',
      frontZip: true,
    },
    fleece: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_539c5217-5e21-4ba8-b084-c270b53b3e99.png?v=1787011259',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_a0dd44b5-0e6a-4bb9-9d77-bf1f82bd72ea.png?v=1787011259',
      side: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_2f22f8f8-c12f-4302-972f-6730d5dc093c.png?v=1787011259',
      frontZip: true,
    },
    waterproof: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_539c5217-5e21-4ba8-b084-c270b53b3e99.png?v=1787011259',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_a0dd44b5-0e6a-4bb9-9d77-bf1f82bd72ea.png?v=1787011259',
      side: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224858_2f22f8f8-c12f-4302-972f-6730d5dc093c.png?v=1787011259',
      frontZip: true,
    },
    // Round 29 (2026-08-21, Wyan): new categories — tunic and scrub top, requested for
    // healthcare/medical workwear products ("I need to create something for tunics and
    // scrub tops"). Two SEPARATE categories per Wyan's call, each with its own AI-generated
    // outline stencil set (V-neck front, round/crew-neck back — a garment's back never
    // matches its V-neck front, corrected after Wyan flagged the first back-view attempt),
    // `side` mirrored via CSS/SVG transform for Left Sleeve/Right Sleeve same as
    // softshell/fleece/waterproof above. No `categoryBadges` overrides added — both start
    // on the shared base badge positions (Wyan's call: "Start with defaults") and fall
    // through via the existing getBadge() resolver until/unless he asks for per-category
    // nudges like the ones fleece received above.
    tunic: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/branding-wizard-tunic-front.png?v=1787317473',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/branding-wizard-tunic-back.png?v=1787317475',
      side: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/branding-wizard-tunic-side.png?v=1787317473',
    },
    scrub: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/branding-wizard-scrub-front.png?v=1787317473',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/branding-wizard-scrub-back.png?v=1787317473',
      side: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/branding-wizard-scrub-side.png?v=1787317475',
    },
    polo: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224949_45d17a16-17dd-4a1e-8258-f8d4bb07cefd.png?v=1787007812',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224949_9e497373-414a-426f-bcfc-c522304a1d17.png?v=1787007812',
      left: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225023_a4124016-40ef-4d19-9969-19ad739b7604.png?v=1787007813',
      right: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225023_80cc7068-34e3-4561-8780-1384bc731fbd.png?v=1787007812',
    },
    tshirt: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224949_38650f47-de0e-4d07-a89c-417f41f28ab8.png?v=1787007812',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224949_d8ee3b0a-3168-41b0-a54b-36c96e4751cb.png?v=1787007813',
      left: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225023_75befbdf-8ffe-4fcf-a309-e9becd81e490.png?v=1787007812',
      right: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224949_428ac48f-bdb0-4bfd-9883-6fb3a86dc21e.png?v=1787007812',
    },
    // Long-sleeve t-shirt — Wyan's call, 2026-08-24: a Hi-Vis long-sleeve tee was rendering
    // with the plain short-sleeve tshirt stencil, so it needed its own AI-generated image set
    // rather than reusing 'tshirt'. Derived from the same short-sleeve tshirt stencils via an
    // instruction-based AI edit (sleeves extended to the wrist), same front/back/left/right
    // shape as 'tshirt' above — not sleeveless, no front zip.
    tshirtls: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260824_151848_dc4f5713-3208-40ac-84d0-8bdc7ad7b177.png?v=1787607252',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260824_151905_fb16ce67-eae6-4f55-b17f-ee7cff31e86b.png?v=1787607251',
      left: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260824_151908_6356f9f2-b732-4385-8010-80a774f9bf8b.png?v=1787607251',
      right: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260824_151910_1329ccab-c890-4617-ba16-e5da4f917ffc.png?v=1787607251',
    },
    hoodiefz: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224949_bc39bc26-5bc1-473e-ab22-e5a00b0e1996.png?v=1787007812',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225023_346f277d-af56-465d-989a-522aec90eed5.png?v=1787007813',
      left: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_224949_96795d87-faf4-4064-a3b7-3fd9c01fc7dc.png?v=1787007812',
      right: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225023_181a67f4-7caa-49d1-8801-608501fd7bfb.png?v=1787007812',
      frontZip: true,
    },
    hoodienz: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225102_906dca98-9132-41a1-b1ef-f8c0938cb788.png?v=1787007813',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225102_6acfede1-267d-4300-bb14-032577559900.png?v=1787007812',
      left: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225102_6398bcf1-4518-446b-ab45-e4a66d0bede6.png?v=1787007812',
      right: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225102_479198bd-ead6-49cf-a9af-6995aa53d8a1.png?v=1787007812',
    },
    sweatshirt: {
      front: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225102_91c2e15a-4f87-4a56-985f-f3860cb23b66.png?v=1787007813',
      back: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225102_23beceb5-f4ea-437c-ae4d-11cccd81678b.png?v=1787007812',
      left: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225102_5196420a-13fd-479d-add6-2308b6f7d757.png?v=1787007812',
      right: 'https://cdn.shopify.com/s/files/1/0980/8173/5033/files/hf_20260817_225140_465c2870-4282-49e7-a0f8-764dd13c7c8a.png?v=1787007812',
    },
  };
  const DEFAULT_CATEGORY = 'tshirt';

  // Trousers don't take branding (no logo/embroidery position makes sense on a pair of
  // work trousers) — Wyan's call, 2026-08-24: "Remove all branding adding options on any
  // trousers product." Matched by title keyword, same convention as detectCategory() above,
  // since product_type is unused across the Rosewear catalog. Covers "Trouser"/"Trousers",
  // "Pant"/"Pants", and "Bottom"/"Bottoms" — Wyan flagged 2026-08-26 that "Mens Rosewear
  // Hi-Vis Workforce Bottoms" still showed the wizard because its title has no "trouser"/
  // "pant" keyword, only "Bottoms" ("the high-vis trousers or bottoms ... it hasn't been
  // removed from the bottoms"). This also now correctly excludes scrub bottoms
  // ("Ladies Rosewear Lux Clinic Scrub Straight Leg Bottoms") for the same reason — no
  // logo/embroidery position makes sense on any legwear.
  function isTrousersProduct(title) {
    const t = (title || '').toLowerCase();
    return /trouser|\bpants?\b|\bbottoms?\b/.test(t);
  }

  // Category is detected from the product title via keyword matching — Shopify's
  // product_type field is unused across the whole Rosewear catalog (only one internal
  // value exists store-wide), so it can't drive this. Checked in order: hoodie must be
  // tested before sweatshirt/jacket so a "Zip Hoodie" title doesn't fall through to a
  // generic bucket, and full-zip vs no-zip is a second check within the hoodie match.
  function detectCategory(title) {
    const t = (title || '').toLowerCase();
    // Hi-Vis + Waterproof combo (e.g. "Mens Rosewear Workforce Hi-Vis Waterproof Jacket")
    // must be checked BEFORE the standalone hi-vis check below — Wyan's call, 2026-08-18:
    // this product was rendering with the sleeveless Gilet/Hi-Vis vest images (no sleeve
    // positions, Big Front/Centre Chest hidden) when it's actually a full jacket and should
    // use the same images/behaviour as Softshell (sleeved, front-zip). Any title matching
    // both "hi-vis" and "waterproof" routes to 'waterproof' instead of the plain 'hivis'
    // sleeveless-vest bucket.
    if (/his?[\s-]?vis/.test(t) && /waterproof|rain\s?coat|shell\s?jacket/.test(t)) return 'waterproof';
    if (/gilet/.test(t)) return 'gilet';
    if (/softshell/.test(t)) return 'softshell';
    if (/fleece/.test(t)) return 'fleece';
    if (/waterproof|rain\s?coat|shell\s?jacket/.test(t)) return 'waterproof';
    // Tunic and scrub top (medical/healthcare workwear) — Round 29, Wyan's call. Checked
    // before polo/tshirt so a "Scrub Top" or "Nurses Tunic" title doesn't fall through to
    // the generic tee bucket.
    if (/tunic/.test(t)) return 'tunic';
    if (/scrub/.test(t)) return 'scrub';
    if (/polo/.test(t)) return 'polo';
    if (/hoodie|hooded/.test(t)) {
      return /full[\s-]?zip|zip[\s-]?up|zipped/.test(t) ? 'hoodiefz' : 'hoodienz';
    }
    if (/sweatshirt|jumper/.test(t)) return 'sweatshirt';
    // "Hi-Vis" describes a fabric/finish, not a garment shape, so a sleeved Hi-Vis product
    // (e.g. "Mens Rosewear Hi-Vis Workforce Long Sleeve T-Shirt") must resolve to its actual
    // sleeved shape (t-shirt here) rather than the sleeveless vest images — this check must
    // come BEFORE the standalone hi-vis fallback below, same reasoning as the waterproof
    // combo above (Wyan flagged the Long Sleeve T-Shirt rendering with the sleeveless
    // Gilet/Hi-Vis stencil, 2026-08-24).
    // Long-sleeve t-shirt/tee — must be checked before the generic t-shirt check below so a
    // "Hi-Vis Workforce Long Sleeve T-Shirt" title routes to the long-sleeve stencil set
    // instead of the plain short-sleeve one (Wyan's call, 2026-08-24: "Use the long sleeve
    // images and add to the widget").
    if (/long[\s-]?sleeve/.test(t) && /t-?shirt|\btee\b/.test(t)) return 'tshirtls';
    if (/t-?shirt|\btee\b/.test(t)) return 'tshirt';
    if (/jacket/.test(t)) return 'softshell';
    // Standalone hi-vis (vests/waistcoats with no other garment-shape keyword in the title)
    // falls through to here LAST, after every specific garment-shape check above has had a
    // chance to match — "hi-vis" only means "sleeveless vest" when nothing more specific
    // matched first. Matches both "hi-vis" and the "his-vis" typo that appears on at least
    // one real Rosewear catalog product ("His-Vis Demo Product") — confirmed via a live
    // category catalog check earlier in this project.
    if (/his?[\s-]?vis/.test(t)) return 'hivis';
    return DEFAULT_CATEGORY;
  }

  // view: which stencil image to show (front/back/left/right — see GARMENT_IMAGE_SETS).
  // badge: {cx, cy, r} — the "logo goes here" marker's centre and radius, in the shared
  // 0–80 × 0–100 coordinate space every stencil photo is rendered into (matches their
  // shared 4:5 aspect ratio, so one badge position works across all garment categories
  // without per-category tuning). embroideryBlocked flags positions too large/costly to
  // embroider well. sleeved flags positions hidden for sleeveless categories (gilet, hivis).
  //
  // IMPORTANT — Left/Right follow garment convention (the wearer's own left/right), not
  // screen left/right — same convention every embroiderer/printer uses. Left/Right Sleeve
  // now use the dedicated left-side/right-side profile photo for that garment (rather than
  // badging a sleeve position onto the front photo), since a real side-view accurately
  // shows where a sleeve print actually sits.
  //
  // Badge coordinates are estimates based on how consistently the stencil photos were
  // generated (centred, similar margins/framing) — not yet pixel-calibrated per image, so
  // expect a quick nudge pass once these are live on the preview theme, same as the
  // hand-drawn badges were tuned earlier.
  //
  // COORDINATES SPLIT PER CATEGORY (Wyan's call, 2026-08-18): `badge` on each position
  // below is the BASE/default coordinate, used by every category unless overridden.
  // `categoryBadges` (optional, per position) maps a category key → its own independent
  // {cx, cy, r} — set one there to nudge a badge for ONE product category without moving
  // it anywhere else. This replaced the old fully-global scheme after a round-8 Big
  // Front/Centre Chest nudge aimed at the no-zip hoodie also shifted those same two
  // badges on T-Shirt, Polo, Sweatshirt, and the full-zip hoodie (every category that
  // shares those two positions) — Wyan only wanted the no-zip hoodie to change.
  // getBadge(pos, category) below resolves which one applies; introducing this mechanism
  // does not itself move any badge — every category still resolves to the exact same
  // base value it had immediately beforehand until a categoryBadges override is added.
  function getBadge(pos, category) {
    return (pos.categoryBadges && pos.categoryBadges[category]) || pos.badge;
  }
  // getLabel mirrors getBadge, but for the customer-facing NAME of a position instead of
  // its coordinates. `categoryLabels` (optional, per position) maps a category key → its
  // own display label, e.g. Round 15 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only: "Change
  // it to lower back instead of nape of neck") — the shared position key stays 'Nape Of
  // Neck' (so occupied-position locking and the cart's 'Branding Position' property both
  // keep working off one stable identifier — see the two getLabel() call sites below), but
  // customers configuring THIS category see "Lower Back" everywhere it's shown, and that's
  // also what gets written to the cart line's 'Branding Position' property for this
  // category, so production sees the right placement name too. Every other category still
  // falls back to the position's own key, unchanged.
  function getLabel(pos, category) {
    return (pos.categoryLabels && pos.categoryLabels[category]) || pos.key;
  }
  // Round 30 (Wyan, t-shirt-only — the 'tshirt' category, 2026-08-24: "Move the left chest over to the left a bit. Move the right chest over to the right a bit. Move the centre of the chest up a bit. Left sleeve slightly to the left and up a bit. Right sleeve slightly to the right and up a bit. Move nape of neck up a bit") — new categoryBadges.tshirt entries on all six positions ("a bit"/"slightly" ≈ 3 units, same convention as prior rounds): Left Chest cx 50→47 (left), Right Chest cx 30→33 (right), Centre Chest cy 48→45 (up), Left Sleeve cx 44→41 (left) and cy 40→37 (up), Right Sleeve cx 36→39 (right) and cy 40→37 (up), Nape Of Neck cy 34→31 (up). Doesn't touch any other category's overrides or the base used by polo/tunic/scrub/tshirtls/etc.
  // Round 31 (Wyan, t-shirt-only, 2026-08-24: "move right chest to 35. move left chest to 45. move centre of chest up to 42. move nape of neck up to 30" — explicit absolute targets given, not magnitude words) — categoryBadges.tshirt updated: Left Chest cx 47→45, Right Chest cx 33→35, Centre Chest cy 45→42, Nape Of Neck cy 31→30. Left Sleeve/Right Sleeve untouched this round. Doesn't touch any other category's overrides or the base used by polo/tunic/scrub/tshirtls/etc.
  // Round 32 (Wyan, scrub-only — the 'scrub' category, shared by scrub tops and
  // scrub sets: "the nape of the neck needs to go up quite a lot. The right sleeve
  // needs to go up a little bit, and the left sleeve needs to go up a little bit") —
  // new categoryBadges.scrub entries (first override for this category on these
  // positions), nudged up from the shared base (up means cy decreases): Nape Of Neck
  // cy 34→24 ("quite a lot" ≈ 10 units, same round-6-precedent convention); Left
  // Sleeve cy 40→37, Right Sleeve cy 40→37 ("a little bit" ≈ 3 units, same
  // convention as prior "a bit"/"little bit" rounds). cx untouched on all three (no
  // left/right request this round). Doesn't touch the sweatshirt/waterproof/hoodiefz/
  // hoodienz/gilet/fleece/tshirt overrides or the base used by every other category.
  // Round 33 (Wyan, softshell-only — the 'softshell' category, which shares an image
  // set with fleece/waterproof but has its own separate categoryBadges key: "Move the
  // left sleeve over to the left abit and the right sleeve over to the right abit on
  // the softshell jacket logo position") — new categoryBadges.softshell entry (first
  // override for this category on these positions), spread out from the shared base
  // cx:44/36 ("a bit" ≈ 3 units, same convention as prior "a bit"/"little bit" rounds):
  // Left Sleeve cx 44→41, Right Sleeve cx 36→39. cy untouched (no vertical request
  // this round, stays at base 40). Doesn't touch the sweatshirt/waterproof/hoodiefz/
  // hoodienz/fleece/tshirt/scrub overrides or the base used by every other category.
  // Round 34 (Wyan, 2026-08-27 -- softshell/scrub/tunic all in one message: "On the
  // Softshell Jacket ... move the left chest over to the left a tiny bit ... right
  // chest over to the right a tiny bit ... left sleeve needs to move over to the left
  // quite a bit ... right sleeve needs to move over to the right quite a bit. On the
  // scrub ... remove the big front ... nape of the neck needs to go up quite a bit
  // higher ... right sleeve needs to go up quite a bit ... left sleeve needs to go up
  // quite a bit. On the tunic ... push the left chest up a little bit ... right chest
  // up a little bit ... centre chest up a little bit ... remove the big front again
  // ... left sleeve needs to go up quite a bit ... right sleeve needs to go up quite a
  // bit ... nape of neck needs to go up quite a lot") --
  // Softshell: new categoryBadges.softshell entries for Left/Right Chest (first
  // override for these two positions on this category), nudged from shared base
  // cx:50/30 ("a tiny bit" ~= 1 unit): Left Chest cx 50->49, Right Chest cx 30->31.
  // Existing softshell Left/Right Sleeve overrides (from Round 33) spread further
  // apart ("quite a bit" ~= 10 units): Left Sleeve cx 41->31, Right Sleeve cx 39->49.
  // Scrub: Big Front now hidden for this category (new hiddenCategories:['scrub',
  // 'tunic'] field on the Big Front position, checked in renderPositionGrid's
  // visiblePositions filter alongside the existing sleeved/blockedByFrontZip checks).
  // Existing scrub Nape Of Neck/Left Sleeve/Right Sleeve overrides (from Round 32)
  // nudged further up ("quite a bit"/"quite a bit higher" ~= 10 units, up means cy
  // decreases): Nape Of Neck cy 24->14, Left Sleeve cy 37->27, Right Sleeve cy 37->27.
  // cx untouched on all three (no left/right request this round).
  // Tunic: first-ever categoryBadges.tunic entries on all six positions (previously
  // pure base, no override) -- Left Chest cy 40->37, Right Chest cy 40->37, Centre
  // Chest cy 48->45 ("a little bit" ~= 3 units, up means cy decreases; cx unchanged
  // on all three); Left Sleeve cy 40->30, Right Sleeve cy 40->30, Nape Of Neck
  // cy 34->24 ("quite a bit"/"quite a lot" ~= 10 units; cx unchanged on all three).
  // Big Front hidden via the same hiddenCategories:['scrub','tunic'] field as scrub,
  // above. Doesn't touch any other category's overrides or the base used by every
  // other category.
  // Round 35 (Wyan, 2026-08-27 -- "The right sleeve on the Softshell jacket needs to
  // go over quite a bit to the left now, and the left sleeve needs to go over quite a
  // bit to the right. And the nape of the neck on the tunic needs to go up quite a
  // bit.") -- Softshell: categoryBadges.softshell updated ("quite a bit" ~= 10 units):
  // Left Sleeve cx 31->41 (right), Right Sleeve cx 49->39 (left) -- reverses the
  // direction of Round 34's spread-apart move, pulling them back toward each other
  // and now crossing past center (only 2 units apart). cy untouched on both (stays
  // at base 40). Tunic: categoryBadges.tunic.Nape Of Neck nudged further up
  // ("quite a bit" ~= 10 units, up means cy decreases): cy 24->14. Doesn't touch any
  // other category's overrides or the base used by every other category.
  const positions = [
    // Left/Right Chest history (2026-08-18): cx 50/30 (original) → 46/34 (round 2) →
    // 43/37 (round 3, toward centre) → 46/34 (round 4, further apart again) → 48/32
    // (round 5, "left chest move to right a bit, right chest move to left a bit") → 50/30
    // (round 6, same request repeated — spread a touch further still, back to the original
    // cx spread). cy 32 (original) → 38 (round 2) → 44 (round 3, "come down") → 40
    // (round 5, "up a bit" — round 3 came down slightly too far). Round 6 didn't touch cy.
    // Round 9 (Wyan, sweatshirt-only: "move the left chest a bit more to the right, move the
    // right chest a bit over to the left") — added via categoryBadges rather than touching
    // the shared base, per Wyan's "Split the co-ordinates" instruction, so T-Shirt/Polo/
    // hoodies etc. keep the base cx:50/30 unchanged.
    // Round 10 (Wyan, sweatshirt-only: "move the left chest a lot more to the left, move the
    // right chest a lot more to the right") — reverses round 9's outward spread, pulling both
    // sweatshirt-only badges back in toward centre by a larger amount. Still categoryBadges
    // only — base cx:50/30 (every other category) untouched. This left them only 2 units
    // apart (41/39) with an 8-unit radius each — visibly overlapping.
    // Round 11 (Wyan, sweatshirt-only: "left chest slightly more to the right, right chest
    // slightly to the left") — spreads them back apart a touch, easing the round-10 overlap.
    // Still categoryBadges only.
    // Round 12 (Wyan, Hi-Vis Waterproof Jacket-only — the 'waterproof' category: "move the
    // left chest ever so slightly to the left and the right chest ever so slightly to the
    // right") — new categoryBadges.waterproof entry (first override for this category on
    // these positions), nudged out from the shared base cx:50/30. Doesn't touch the
    // sweatshirt overrides or the base used by every other category.
    // Round 14 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only — the 'hoodiefz' category:
    // "the left chest needs to come down a little bit, the right chest also needs to come
    // down a little bit") — new categoryBadges.hoodiefz entry, nudged down from the shared
    // base cy:40. Doesn't touch the sweatshirt/waterproof overrides or the base used by
    // every other category.
    // Round 16 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only: "Left chest and right chest
    // can ever so slightly come down. And also, the left chest can move a tiny bit to the
    // left, and the right just a tiny bit to the right") — hoodiefz-only: cy 43→44 on both
    // ("ever so slightly" ≈ 1 unit, same convention as prior ever-so-slightly rounds); cx
    // 50→49 on Left Chest and 30→31 on Right Chest ("a tiny bit" treated as the same small
    // ≈1-unit nudge as "ever so slightly", smaller than "a bit"/"slightly"). Doesn't touch
    // the sweatshirt/waterproof overrides or the base used by every other category.
    // Round 17 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only: "Move both left and right chest
    // down to 46") — hoodiefz-only, cy 44→46 on both (explicit absolute target given, not an
    // inferred magnitude). Doesn't touch the sweatshirt/waterproof overrides or the base used
    // by every other category.
    // Round 19 (Wyan, Men's Rosewear Pullover Hoodie (No Zip)-only — the 'hoodienz' category:
    // "The left chest to come down a little bit ... the right chest to come down a little
    // bit ... the left chest to move to the left a little bit ... the right chest to move to
    // the right a little bit") — new categoryBadges.hoodienz entry (first override for this
    // category on these positions), nudged from the shared base cx:50/30, cy:40 ("a little
    // bit" ≈ 3 units, same convention as prior "a bit"/"little bit" rounds): Left Chest
    // cx 50→47 (toward screen-left), cy 40→43 (down); Right Chest cx 30→33 (toward
    // screen-right), cy 40→43 (down). Doesn't touch the sweatshirt/waterproof/hoodiefz
    // overrides or the base used by every other category.
    // Round 21 (Wyan, gilet-only — the 'gilet' category: "the left chest and right chest
    // need to go up a bit ... the left chest needs to come over slightly a bit more to the
    // left, and the right chest needs to come right slightly as well") — categoryBadges.gilet
    // entry (added in Round 20) nudged further: cy 40→37 on both ("a bit" ≈ 3 units, same
    // convention as prior "a bit"/"little bit" rounds — up means cy decreases); cx 49→46 on
    // Left Chest (further toward screen-left) and 31→34 on Right Chest (further toward
    // screen-right), 3 units each, matching the "a bit"/"slightly" convention. Doesn't touch
    // the sweatshirt/waterproof/hoodiefz/hoodienz overrides or the base used by every other
    // category.
    // Round 22 (Wyan, fleece-only — the 'fleece' category: "the left chest needs to move
    // over quite a bit to the left ... the right chest needs to move over quite a bit to
    // the right") — new categoryBadges.fleece entry (first override for this category on
    // these positions), spread out from the shared base cx:50/30 ("quite a bit" ≈ 10 units,
    // same round-6-precedent convention): Left Chest cx 50→40, Right Chest cx 30→40 — both
    // land dead-centre from opposite directions. cy untouched (no vertical request this
    // round, stays at base 40). Doesn't touch the sweatshirt/waterproof/hoodiefz/hoodienz/
    // gilet overrides or the base used by every other category.
    // Round 25 (Wyan, fleece-only: "Move the left chest over to the right by 10 ... Move
    // the right chest over to the left by 10" — explicit numeric shifts given, not
    // inferred magnitude words) — moves the Round-22 dead-centre pair (both cx:40) apart
    // again: Left Chest cx 40→50, Right Chest cx 40→30 — back to the shared base
    // cx:50/30. cy untouched, stays at base 40. Doesn't touch the sweatshirt/waterproof/
    // hoodiefz/hoodienz/gilet overrides or the base used by every other category.
    // Round 27 (Wyan, fleece-only: "Move the left chest to 48 ... Move the right chest to
    // 32" — explicit absolute targets given, not a magnitude or a relative shift) — small
    // pull-in from Round 25: Left Chest cx 50→48, Right Chest cx 30→32. cy untouched,
    // stays at base 40. Doesn't touch the sweatshirt/waterproof/hoodiefz/hoodienz/gilet
    // overrides or the base used by every other category.
    { key: 'Left Chest', tag: 'Popular', view: 'front', badge: { cx: 50, cy: 40, r: 8 }, categoryBadges: { sweatshirt: { cx: 43, cy: 40, r: 8 }, waterproof: { cx: 49, cy: 40, r: 8 }, hoodiefz: { cx: 49, cy: 46, r: 8 }, hoodienz: { cx: 47, cy: 43, r: 8 }, gilet: { cx: 46, cy: 37, r: 8 }, fleece: { cx: 48, cy: 40, r: 8 }, tshirt: { cx: 45, cy: 40, r: 8 }, softshell: { cx: 49, cy: 40, r: 8 }, tunic: { cx: 50, cy: 37, r: 8 } } },
    { key: 'Right Chest', tag: '', view: 'front', badge: { cx: 30, cy: 40, r: 8 }, categoryBadges: { sweatshirt: { cx: 37, cy: 40, r: 8 }, waterproof: { cx: 31, cy: 40, r: 8 }, hoodiefz: { cx: 31, cy: 46, r: 8 }, hoodienz: { cx: 33, cy: 43, r: 8 }, gilet: { cx: 34, cy: 37, r: 8 }, fleece: { cx: 32, cy: 40, r: 8 }, tshirt: { cx: 35, cy: 40, r: 8 }, softshell: { cx: 31, cy: 40, r: 8 }, tunic: { cx: 30, cy: 37, r: 8 } } },
    // Centre Chest sits dead-centre (cx:40) same as Big Front, so it's directly over the
    // zip line on any front-zip garment too — blocked for the same set of categories
    // (gilet, hivis, softshell, fleece, waterproof, full-zip hoodie). Wyan's call, 2026-08-18.
    // cy nudged down quite a bit in round 6 (38→48, Wyan: "centre chest needs to come down
    // quite a bit"). Round 8 (Wyan re: no-zip hoodie: "big front and centre of chest ...
    // still needs to come down") pushed it to cy:56 GLOBALLY at first, which unintentionally
    // moved it on T-Shirt/Polo/Sweatshirt/full-zip hoodie too, even though Wyan said
    // everything else was already correct. Now split per-category (see categoryBadges
    // comment above `positions`): base stays at round 6's cy:48 for every other category,
    // and only hoodienz gets round 8's cy:56.
    // Round 19 (Wyan, Men's Rosewear Pullover Hoodie (No Zip)-only: "The center chest needs
    // to come up quite a bit") — hoodienz-only, cy 56→46 ("quite a bit" ≈ 10 units, matching
    // the round-6 precedent for this exact phrase: cy 38→48). Doesn't touch the base used by
    // every other category.
    { key: 'Centre Chest', tag: '', view: 'front', badge: { cx: 40, cy: 48, r: 9 }, blockedByFrontZip: true, categoryBadges: { hoodienz: { cx: 40, cy: 46, r: 9 }, tshirt: { cx: 40, cy: 42, r: 9 }, tunic: { cx: 40, cy: 45, r: 9 } } },
    // Left/Right Sleeve badge history (2026-08-18): cy:32 (original) → cy:46 (round 1, Wyan
    // flagged it landing near the hood/collar/shoulder) → cy:38 (round 2, "up the arm a bit"
    // — this overshot and put it back off the sleeve, onto the shoulder) → cy:52 (round 3,
    // "move back more so it's actually on the sleeve" — reverting past round 1's cy:46 to
    // sit further down, clearly on the sleeve fabric rather than the shoulder seam). cy
    // untouched since round 3.
    // cx split apart in round 4 (Wyan: "left sleeve needs to move to right a bit and right
    // sleeve needs to move over to left a bit") — Left/Right Sleeve previously shared cx:40
    // dead-centre (the badge circle itself is never mirrored, so both sides landed at the
    // exact same screen position — see garmentSvg()'s mirror-transform comment). They now
    // have independent cx values so each can be nudged onto its own side-view image.
    // Round 6 repeated the same direction of request (cx 44→47 / 36→33) — nudged further
    // apart again. Round 7 (Wyan: "left sleeve move to left a bit and up quite a bit, right
    // sleeve move to right a bit and up quite a bit") reversed the cx direction again
    // (47→44 / 33→36 — back toward round 4's split) and pulled cy up quite a bit (52→40,
    // undoing most of round 3's downward move — round 3 had gone too far down onto the
    // lower arm/cuff area).
    // Round 9 (Wyan, sweatshirt-only: "move the left sleeve slightly to the right and the
    // right sleeve slightly to the left") — added via categoryBadges only; base cx:44/36
    // stays untouched for every other sleeved category.
    // Round 10 (Wyan, sweatshirt-only: "move the left sleeve a bit more to the left and move
    // the right sleeve a bit more to the right") — reverses round 9's direction, narrowing
    // the sweatshirt-only gap back down slightly. Still categoryBadges only.
    // Round 12 (Wyan, Hi-Vis Waterproof Jacket-only — the 'waterproof' category: "the left
    // sleeve needs to move to the left a bit, and the right sleeve needs to move to the
    // right a bit") — new categoryBadges.waterproof entry, spread out from the shared base
    // cx:44/36. Doesn't touch the sweatshirt overrides or any other category's base.
    // Round 13 (Wyan, Hi-Vis Waterproof Jacket-only: "the left sleeve needs to move over a
    // bit more to the left, and the right sleeve needs to move over a bit more to the
    // right") — spreads the waterproof-only pair further apart again. Still categoryBadges
    // only.
    // Round 14 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only — the 'hoodiefz' category: "the
    // left sleeve needs to come down a little bit, the right sleeve needs to come down a
    // little bit") — new categoryBadges.hoodiefz entry, nudged down from the shared base
    // cy:40. cx left untouched (no left/right request this round). Doesn't touch the
    // sweatshirt/waterproof overrides or the base used by every other sleeved category.
    // Round 16 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only: "The left sleeve and the right
    // sleeve also need to come lower") — hoodiefz-only, cy 43→49 on both (unqualified "come
    // lower", no magnitude word given — treated the same as the round-3 unqualified "needs
    // to come down" precedent, ≈6 units). cx untouched. Doesn't touch the sweatshirt/
    // waterproof overrides or the base used by every other sleeved category.
    // Round 19 (Wyan, Men's Rosewear Pullover Hoodie (No Zip)-only: "The left sleeve and the
    // right sleeve need to come down quite a bit") — new categoryBadges.hoodienz entry,
    // cy 40→50 on both ("quite a bit" ≈ 10 units, same round-6-precedent convention as the
    // Centre Chest change above). cx untouched (no left/right request this round). Doesn't
    // touch the sweatshirt/waterproof/hoodiefz overrides or the base used by every other
    // sleeved category.
    // Round 23 (Wyan, fleece-only — the 'fleece' category: "the left sleeve needs to move
    // quite a lot over to the left ... the right sleeve needs to move over quite a lot to
    // the right") — new categoryBadges.fleece entry (first override for this category on
    // these positions), spread out from the shared base cx:44/36 ("quite a lot" treated the
    // same as "quite a bit" ≈ 10 units, same round-6-precedent convention): Left Sleeve
    // cx 44→34, Right Sleeve cx 36→46. cy untouched (no vertical request this round, stays
    // at base 40). Doesn't touch the sweatshirt/waterproof/hoodiefz/hoodienz overrides or
    // the base used by every other sleeved category.
    // Round 24 (Wyan, fleece-only: "the left sleeve needs to move over quite a bit to the
    // right, and the right sleeve needs to move over to the left" [quite a bit, carried
    // from the first clause]) — pulls the Round-23 fleece pair back toward center by 10
    // units each: Left Sleeve cx 34→44 (back to the shared base), Right Sleeve cx 46→36
    // (back to the shared base). cy untouched, stays at base 40. Doesn't touch the
    // sweatshirt/waterproof/hoodiefz/hoodienz overrides or the base used by every other
    // sleeved category.
    // Round 26 (Wyan, fleece-only: "Move the left sleeve over to the left by 5 ... Move
    // the right sleeve over to the right by 5" — explicit numeric shifts given, not
    // inferred magnitude words) — nudges the Round-24 shared-base pair apart again:
    // Left Sleeve cx 44→39, Right Sleeve cx 36→41. cy untouched, stays at base 40.
    // Doesn't touch the sweatshirt/waterproof/hoodiefz/hoodienz overrides or the base
    // used by every other sleeved category.
    // Round 28 (Wyan, fleece-only: "Move the left sleeve to 38 ... Move the right sleeve
    // to 42" — explicit absolute targets given, not a magnitude or a relative shift) —
    // small pull-in from Round 26: Left Sleeve cx 39→38, Right Sleeve cx 41→42. cy
    // untouched, stays at base 40. Doesn't touch the sweatshirt/waterproof/hoodiefz/
    // hoodienz overrides or the base used by every other sleeved category.
    { key: 'Left Sleeve', tag: '', view: 'left', badge: { cx: 44, cy: 40, r: 7 }, sleeved: true, categoryBadges: { sweatshirt: { cx: 43, cy: 40, r: 7 }, waterproof: { cx: 38, cy: 40, r: 7 }, hoodiefz: { cx: 44, cy: 49, r: 7 }, hoodienz: { cx: 44, cy: 50, r: 7 }, fleece: { cx: 38, cy: 40, r: 7 }, tshirt: { cx: 41, cy: 37, r: 7 }, scrub: { cx: 44, cy: 27, r: 7 }, softshell: { cx: 41, cy: 40, r: 7 }, tunic: { cx: 44, cy: 30, r: 7 } } },
    { key: 'Right Sleeve', tag: '', view: 'right', badge: { cx: 36, cy: 40, r: 7 }, sleeved: true, categoryBadges: { sweatshirt: { cx: 37, cy: 40, r: 7 }, waterproof: { cx: 42, cy: 40, r: 7 }, hoodiefz: { cx: 36, cy: 49, r: 7 }, hoodienz: { cx: 36, cy: 50, r: 7 }, fleece: { cx: 42, cy: 40, r: 7 }, tshirt: { cx: 39, cy: 37, r: 7 }, scrub: { cx: 36, cy: 27, r: 7 }, softshell: { cx: 39, cy: 40, r: 7 }, tunic: { cx: 36, cy: 30, r: 7 } } },
    // Big Front: large chest print area — print only (can't be embroidered cleanly at this
    // size), and hidden entirely for any garment with a front zip (gilet, hivis, softshell,
    // fleece, waterproof, full-zip hoodie — see `frontZip` on GARMENT_IMAGE_SETS), since the
    // zip runs straight through the middle of the print area and there's no clean way to
    // place a large front print/embroidery over it (Wyan's call).
    // Nudged up from cy:50 (2026-08-18, Wyan's second round of feedback), then down a bit
    // again in round 6 (42→46, Wyan: "big chest [Big Front] needs to come down a bit too").
    // Round 8 (Wyan re: no-zip hoodie: "big front and centre of chest ... still needs to
    // come down") pushed it to cy:54 GLOBALLY at first, which unintentionally moved it on
    // T-Shirt/Polo/Sweatshirt/full-zip hoodie too, even though Wyan said everything else
    // was already correct. Now split per-category (see categoryBadges comment above
    // `positions`): base stays at round 6's cy:46 for every other category, and only
    // hoodienz gets round 8's cy:54.
    { key: 'Big Front', tag: '', view: 'front', badge: { cx: 40, cy: 46, r: 15 }, embroideryBlocked: true, blockedByFrontZip: true, hiddenCategories: ['scrub', 'tunic'], categoryBadges: { hoodienz: { cx: 40, cy: 54, r: 15 } } },
    // Big Back: same large-format print-only placement, but the back panel has no zip on
    // any garment, so it stays available across the board.
    { key: 'Big Back', tag: '', view: 'back', badge: { cx: 40, cy: 46, r: 15 }, embroideryBlocked: true },
    // Nape Of Neck: small patch at the back collar — supports both methods, unlike
    // the two "Big" options.
    // History (2026-08-18): cy:14 (original, right at the top edge) → cy:22 (round 2,
    // "back of the neck or top of the back") → cy:34 (round 3, "needs to come down a lot"
    // — round 2 still wasn't far enough down the back panel).
    // Round 9 (Wyan, sweatshirt-only: "nape of the neck needs to go up quite a lot as well")
    // — added via categoryBadges only; base cy:34 stays untouched for every other category.
    // Round 11 (Wyan, sweatshirt-only: "the nape of the neck needs to move down slightly as
    // well") — small downward correction after round 9 went up quite a lot. Still
    // categoryBadges only.
    // Round 14 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only — the 'hoodiefz' category: "I
    // think, instead of the nape of the neck (because it's a hoodie), maybe it should be at
    // the bottom, at the back of the hoodie") — new categoryBadges.hoodiefz entry moving this
    // position from the back collar (cy:34) down to near the bottom hem of the back panel
    // (cy:86), rather than adding a whole new position — the key/label stays "Nape Of Neck"
    // for now, only its on-screen marker for this one category moves. cx stays centred (40).
    // Doesn't touch the sweatshirt override or the base used by every other category.
    // Round 15 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only: "Change it to lower back
    // instead of nape of neck, but still too far down. It's now not even on the actual
    // hoodie itself, so it needs to go up a bit") — two changes, hoodiefz-only: (1) added
    // categoryLabels.hoodiefz: 'Lower Back' — see getLabel() above `positions` — so this
    // category shows/sends "Lower Back" instead of "Nape Of Neck" everywhere the position
    // name is displayed, while the underlying key stays "Nape Of Neck" for occupied-position
    // locking and cart-property purposes; (2) nudged the marker back up off the bottom hem,
    // cy:86 → 83, since round 14 had pushed it past the edge of the garment photo. Doesn't
    // touch the sweatshirt override or the base used by every other category.
    // Round 16 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only: "The lower back still needs to
    // go up") — hoodiefz-only, cy 83→77 (unqualified "needs to go up", no magnitude word —
    // treated the same as the round-3 unqualified-move precedent, ≈6 units, same as this
    // round's sleeve change below). Doesn't touch the sweatshirt override or the base used
    // by every other category.
    // Round 17 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only: "Move lower back up to 75") —
    // hoodiefz-only, cy 77→75 (explicit absolute target given, not an inferred magnitude).
    // Doesn't touch the sweatshirt override or the base used by every other category.
    // Round 18 (Wyan, Men's Rosewear Luxe Zip-Up Hoodie-only: "lower back needs to go up to
    // 70") — hoodiefz-only, cy 75→70 (explicit absolute target given). Doesn't touch the
    // sweatshirt override or the base used by every other category.
    // Round 19 (Wyan, Men's Rosewear Pullover Hoodie (No Zip)-only: "We need to change it
    // from napalm neck to lower back" — "napalm neck" is clearly a mis-transcription of
    // "nape of neck", read in context as the same request Round 14/15 handled for hoodiefz)
    // — new categoryLabels.hoodienz: 'Lower Back' entry (same getLabel() mechanism as
    // hoodiefz), plus a new categoryBadges.hoodienz entry moving the marker down from the
    // back collar (base cy:34) to the lower back. No coordinate was given, so this starts
    // from hoodiefz's current fully-tuned value (cy:70, after 5 rounds of feedback) rather
    // than hoodiefz's original untuned round-14 starting point (cy:86) — a closer first
    // guess for a visually similar pullover-hoodie back panel. Still just a starting point;
    // expect a nudge pass once it's visible on the preview theme, same as hoodiefz's was.
    // cx stays centred (40). Doesn't touch the sweatshirt/hoodiefz overrides or the base
    // used by every other category.
    // Round 20 (Wyan, gilet-only — the 'gilet' category: "the nape of the neck to go up
    // quite a bit") — new categoryBadges.gilet entry, cy 34→24 ("quite a bit" ≈ 10 units,
    // same round-6-precedent convention). cx stays centred (40). Doesn't touch the
    // sweatshirt/hoodiefz/hoodienz overrides or the base used by every other category.
    { key: 'Nape Of Neck', tag: '', view: 'back', badge: { cx: 40, cy: 34, r: 6 }, categoryBadges: { sweatshirt: { cx: 40, cy: 22, r: 6 }, hoodiefz: { cx: 40, cy: 70, r: 6 }, hoodienz: { cx: 40, cy: 70, r: 6 }, gilet: { cx: 40, cy: 24, r: 6 }, tshirt: { cx: 40, cy: 30, r: 6 }, scrub: { cx: 40, cy: 14, r: 6 }, tunic: { cx: 40, cy: 14, r: 6 } }, categoryLabels: { hoodiefz: 'Lower Back', hoodienz: 'Lower Back' } },
  ];
  const fonts = [
    { key: 'Block Sans', family: '"Anton", Arial, sans-serif', weight: 400, letterSpacing: '.5px' },
    { key: 'Classic Serif', family: '"Playfair Display", Georgia, serif', weight: 700, letterSpacing: 'normal' },
    { key: 'Script', family: '"Dancing Script", cursive', weight: 700, letterSpacing: 'normal' },
    { key: 'Varsity Block', family: '"Bebas Neue", "Arial Narrow", sans-serif', weight: 400, letterSpacing: '1.5px' },
    { key: 'Typewriter', family: '"Courier Prime", "Courier New", monospace', weight: 700, letterSpacing: 'normal' },
  ];
  const threadColours = [
    { key: 'White', hex: '#ffffff' },
    { key: 'Navy', hex: '#152238' },
    { key: 'Black', hex: '#111111' },
    { key: 'Gold', hex: '#c9a227' },
    { key: 'Red', hex: '#8b1e2b' },
    { key: 'Silver', hex: '#c7ccd6' },
  ];

  // The 4 visual "phases" of configuring ONE position. Multi-position runs repeat
  // method/type/configure once per selected position — see `flow` below — but the
  // steps bar always shows just these 4 chips, reflecting the CURRENT position's phase
  // (Wyan's call, 2026-08-18: "let customers select multiple positions in one pass").
  const PHASES = ['position', 'method', 'type', 'configure'];

  let ctx = null;            // the cart item context passed to open()
  // Multiple distinct cart lines can be added in a single submission (e.g. the bulk
  // order widget adding 5x Small + 5x Medium at once creates two separate lines).
  // Each line needs its own branding turn, so open() calls queue up here and are
  // walked through one at a time instead of only ever handling the first one.
  let itemQueue = [];
  let queueActive = false;
  let wiz = {};               // { mode, category, position, method, type, selectedPositions }
  // `selections` holds one working-config object per position key the customer has
  // ticked on the Position step (see `createEmptySelection`) — method/type/logo-or-text
  // details/notes, captured as they move through that position's method → type →
  // configure steps. `flow` is the ordered list of steps for THIS wizard run: one
  // 'position' step, then method/type/configure repeated once per selected position
  // (built by `buildFlow()` the moment they leave the Position step). `stepIdx` indexes
  // into `flow` — this is what lets a customer add Left Chest AND Right Chest (etc) in
  // one sitting instead of completing the wizard, adding to basket, then reopening it
  // via "Add another logo/text" for the second placement.
  let selections = {};
  let flow = [{ kind: 'position' }];
  let stepIdx = 0;
  let wizText = { font: fonts[0], colour: threadColours[0] }; // repointed to the active position's font/colour while on its configure step
  let occupied = [];          // positions already used on this item (from live cart data)
  let isSubmitting = false;

  const $ = (id) => document.getElementById(id);
  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function garmentSvg(pos) {
    const set = GARMENT_IMAGE_SETS[wiz.category] || GARMENT_IMAGE_SETS[DEFAULT_CATEGORY];
    // Left/Right Sleeve: every category now renders from ONE side photo, mirrored via SVG
    // transform for whichever direction isn't the canonical un-mirrored one — no category
    // needs two separately generated side photos any more (Wyan's call, 2026-08-18: the
    // separate left/right generations for Polo, T-Shirt, both Hoodies, and Sweatshirt
    // weren't matching up cleanly, so those now reuse the same mirror technique already
    // built for Softshell/Fleece/Waterproof's shared `side` image). Preference order:
    // set.side (an explicit shared side photo) first, else set.left (categories that still
    // carry separate left/right URLs in GARMENT_IMAGE_SETS — the `right` URL is now unused
    // but left in place for reference). `left` is always the un-mirrored canonical image.
    let imgUrl;
    let mirror = false;
    if (pos.view === 'left' || pos.view === 'right') {
      imgUrl = set.side || set.left || set[pos.view];
      mirror = pos.view === 'right';
    } else {
      imgUrl = set[pos.view] || set.front;
    }
    const b = getBadge(pos, wiz.category);
    // Small "logo goes here" marker — a thin dashed circle plus a single centred dot.
    // Scaled for the shared 0–80 × 0–100 stencil-photo coordinate space (see the
    // `positions` comment above) — smaller stroke/dot than the old hand-drawn-SVG
    // version since this viewBox is roughly half the size. Badge coordinates are never
    // mirrored: cx is centred (40) on every sleeve position, which is a fixed point under
    // a horizontal flip, so the marker lands in the same visual spot either way.
    const badgeHtml = b
      ? `<circle cx="${b.cx}" cy="${b.cy}" r="${b.r}" fill="rgba(200,98,43,0.08)" stroke="#c8622b" stroke-width="0.8" stroke-dasharray="1.8 1.5"/>
         <circle cx="${b.cx}" cy="${b.cy}" r="1.1" fill="#c8622b"/>`
      : '';
    // viewBox 0 0 80 100 matches the stencil photos' shared 4:5 aspect ratio exactly,
    // so badge circles stay perfectly round without any x/y scale correction. The mirror
    // transform flips the image element only (translate(80,0) scale(-1,1) reflects it
    // back into the same 0–80 viewBox) — the badge circle is drawn afterward, untouched.
    const imageTransform = mirror ? ' transform="translate(80,0) scale(-1,1)"' : '';
    return `<svg viewBox="0 0 80 100"><image href="${imgUrl}" x="0" y="0" width="80" height="100" preserveAspectRatio="xMidYMid meet"${imageTransform}/>${badgeHtml}</svg>`;
  }

  /* ---------------- method-step sample photos ---------------- */
  function renderMethodSwatchImages() {
    if (EMBROIDERY_SAMPLE_IMAGE_URL) {
      $('rwb-methodSwatchEmbroidery').innerHTML = `<img src="${EMBROIDERY_SAMPLE_IMAGE_URL}" alt="Embroidery Finish Example">`;
    }
    if (PRINT_SAMPLE_IMAGE_URL) {
      $('rwb-methodSwatchPrint').innerHTML = `<img src="${PRINT_SAMPLE_IMAGE_URL}" alt="Print Finish Example">`;
    }
  }

  /* ---------------- toast ---------------- */
  function showToast() {
    $('rwb-toastImg').src = ctx.imageUrl || '';
    $('rwb-toastName').textContent = ctx.productTitle || '';
    const variantBit = ctx.variantTitle && ctx.variantTitle !== 'Default Title' ? ctx.variantTitle + ' · ' : '';
    $('rwb-toastMeta').textContent = `${variantBit}Qty ${ctx.quantity}`;
    $('rwb-toastOverlay').classList.add('rwb-show');
  }
  function closeToast() { $('rwb-toastOverlay').classList.remove('rwb-show'); }
  // Used by the toast's "skip" controls (X / "I'll do this later" link) — unlike
  // closeToast() alone (which is also used internally when transitioning into the
  // wizard for the SAME item), this marks the current item's flow as done and
  // advances to the next queued item, if any.
  function skipToast() {
    closeToast();
    finishItemFlow();
  }
  function finishItemFlow() {
    queueActive = false;
    ctx = null;
    advanceQueue();
  }
  function advanceQueue() {
    if (queueActive || itemQueue.length === 0) return;
    queueActive = true;
    ctx = itemQueue.shift();
    if (ctx.mode === 'extra') {
      openWizard('extra');
    } else {
      showToast();
    }
  }

  /* ---------------- live cart lookup (for position-locking) ---------------- */
  function fetchCart() {
    return fetch(routeRoot + 'cart.js').then((r) => r.json());
  }
  function occupiedPositionsFor(cartItemKey) {
    return fetchCart().then((cart) => {
      const used = [];
      cart.items.forEach((item) => {
        if (item.key === cartItemKey && item.properties && item.properties['Branding Position']) {
          used.push(item.properties['Branding Position']);
        }
        if (
          item.variant_id === EXTRA_BRANDING_VARIANT_ID &&
          item.properties &&
          item.properties['_Linked item key'] === cartItemKey &&
          item.properties['Branding Position']
        ) {
          used.push(item.properties['Branding Position']);
        }
      });
      return used;
    });
  }

  /* ---------------- per-position working state ---------------- */
  // One of these is created the moment a position is ticked on the Position step, and
  // holds everything the customer configures for it (method, type, logo/text details,
  // notes) independently of whatever other positions they've also selected.
  function createEmptySelection(key) {
    return {
      position: key,
      method: null,
      type: null,
      uploadedFile: null,
      uploadedFilePreviewUrl: '',
      logoAlt: null, // 'no-logo' | 'have-logo' | null
      textLine1: '',
      textLine2: '',
      textLine3: '',
      font: fonts[0],
      colour: threadColours[0],
      notes: '',
    };
  }

  /* ---------------- wizard open/close ---------------- */
  function openWizard(mode) {
    closeToast();
    wiz = { mode, position: null, method: null, type: null, category: detectCategory(ctx.productTitle), selectedPositions: [] };
    selections = {};
    flow = [{ kind: 'position' }];
    stepIdx = 0;
    wizText = { font: fonts[0], colour: threadColours[0] };

    resetDropzone();
    $('rwb-textLine1').value = '';
    $('rwb-textLine2').value = '';
    $('rwb-textLine3').value = '';
    $('rwb-notesField').value = '';
    document.querySelectorAll('input[name=rwb-logoAlt]').forEach((r) => (r.checked = false));
    document.querySelectorAll('.rwb-method-card').forEach((el) => el.classList.remove('rwb-selected'));
    document.querySelectorAll('.rwb-type-card').forEach((el) => el.classList.remove('rwb-selected'));
    $('rwb-configureLogo').classList.remove('rwb-hidden');
    $('rwb-configureText').classList.add('rwb-hidden');

    $('rwb-wizModeLabel').textContent =
      mode === 'included' ? 'Included With Your Order — No Extra Cost' : `Additional Branding — +${EXTRA_BRANDING_PRICE_LABEL} Per Item`;
    $('rwb-sideImg').src = ctx.imageUrl || '';
    $('rwb-sideName').textContent = ctx.productTitle || '';
    const variantBit = ctx.variantTitle && ctx.variantTitle !== 'Default Title' ? ctx.variantTitle + ' · ' : '';
    $('rwb-sideMeta').textContent = `${variantBit}Qty ${ctx.quantity}`;

    occupiedPositionsFor(ctx.cartItemKey).then((used) => {
      occupied = used;
      renderPositionGrid();
      renderPositionSummary();
    });

    updateMethodAvailability();
    renderMethodSwatchImages();
    renderFontList();
    renderColourList();
    updateTextPreview();
    enterStep();
    $('rwb-wizardOverlay').classList.add('rwb-show');
  }
  function closeWizard() {
    $('rwb-wizardOverlay').classList.remove('rwb-show');
    finishItemFlow();
  }

  /* ---------------- position step (multi-select) ---------------- */
  // Customers can now tick more than one position in a single pass (Wyan's call,
  // 2026-08-18) — Left Chest AND Right Chest in one sitting rather than adding one,
  // then reopening the wizard via "Add another logo/text" for the second. Whichever
  // position they ticked FIRST is the one included in the price (only when this
  // wizard run started from a normal add-to-cart, i.e. wiz.mode === 'included' — a
  // run opened via "Add another logo/text" has no free slot left, so every position
  // picked there is chargeable); every other ticked position is the extra-branding
  // add-on. Order is simply the order they were clicked in.
  function renderPositionGrid() {
    const grid = $('rwb-positionGrid');
    const set = GARMENT_IMAGE_SETS[wiz.category] || GARMENT_IMAGE_SETS[DEFAULT_CATEGORY];
    // Sleeveless categories (gilet, hivis) don't have left/right side photos and can't
    // take a sleeve print anyway, so Left/Right Sleeve simply aren't offered for them.
    // Front-zip categories (gilet, hivis, softshell, fleece, waterproof, full-zip hoodie)
    // don't offer Big Front — the zip runs through the middle of that print area.
    const visiblePositions = positions.filter(
      (p) => !(p.sleeved && set.sleeveless) && !(p.blockedByFrontZip && set.frontZip) && !(p.hiddenCategories && p.hiddenCategories.includes(wiz.category))
    );
    grid.innerHTML = visiblePositions
      .map((p) => {
        // Compared against getLabel() (not p.key) — the cart property read back into
        // `occupied` now stores the category-resolved display name (see buildPropertiesFor),
        // which for most positions/categories is identical to p.key anyway, but for
        // categoryLabels overrides (e.g. hoodiefz's "Lower Back") it isn't (Round 15).
        const isOccupied = occupied.indexOf(getLabel(p, wiz.category)) !== -1;
        const selIdx = wiz.selectedPositions.indexOf(p.key);
        const isSelected = selIdx !== -1;
        let infoHtml;
        if (isOccupied) {
          infoHtml = `<div class="rwb-pos-warn">Location Used In Other Logo</div>`;
        } else {
          // Every position supports Print. Only the two large "Big" placements
          // (embroideryBlocked) can't be embroidered — shown directly under the
          // image so customers see it up front, matching the competitor reference,
          // instead of only discovering it later on the Method step.
          const lines = [`<div class="rwb-pos-avail"><span class="rwb-avail-dot"></span>Print Available</div>`];
          if (!p.embroideryBlocked) {
            lines.push(`<div class="rwb-pos-avail"><span class="rwb-avail-dot"></span>Embroidery Available</div>`);
          }
          infoHtml = `<div class="rwb-pos-avail-list">${lines.join('')}</div>`;
        }
        const tagBadgeHtml = p.tag ? `<div class="rwb-pos-badge">${p.tag}</div>` : '';
        let priceBadgeHtml = '';
        if (isSelected) {
          const isFree = wiz.mode === 'included' && selIdx === 0;
          priceBadgeHtml = `<div class="rwb-pos-price-badge ${isFree ? '' : 'rwb-extra'}">${isFree ? 'Included' : '+' + EXTRA_BRANDING_PRICE_LABEL}</div>`;
        }
        return `<div class="rwb-pos-card ${isSelected ? 'rwb-selected' : ''} ${isOccupied ? 'rwb-disabled' : ''}" data-key="${p.key}">
          ${tagBadgeHtml}
          ${priceBadgeHtml}
          ${garmentSvg(p)}
          <div class="rwb-pos-name">${getLabel(p, wiz.category)}</div>
          ${infoHtml}
        </div>`;
      })
      .join('');
    grid.querySelectorAll('.rwb-pos-card:not(.rwb-disabled)').forEach((el) => {
      el.addEventListener('click', () => {
        const key = el.dataset.key;
        const idx = wiz.selectedPositions.indexOf(key);
        if (idx === -1) {
          wiz.selectedPositions.push(key);
          if (!selections[key]) selections[key] = createEmptySelection(key);
        } else {
          wiz.selectedPositions.splice(idx, 1);
          delete selections[key];
        }
        renderPositionGrid();
        renderPositionSummary();
        updateSidebar();
      });
    });
  }
  function renderPositionSummary() {
    const el = $('rwb-positionSummary');
    if (!el) return;
    const n = wiz.selectedPositions.length;
    if (n === 0) {
      el.textContent = '';
      return;
    }
    const extraCount = wiz.mode === 'included' ? Math.max(0, n - 1) : n;
    const freeCount = n - extraCount;
    let costBit;
    if (extraCount > 0) {
      costBit = ` — ${freeCount ? '1 Included, ' : ''}${extraCount} Additional (+${EXTRA_BRANDING_PRICE_LABEL} Each)`;
    } else {
      costBit = ' — Included In Price';
    }
    el.textContent = `${n} Position${n > 1 ? 's' : ''} Selected${costBit}`;
  }

  /* ---------------- flow (position → method/type/configure per selected position) ---------------- */
  function buildFlow() {
    const f = [{ kind: 'position' }];
    wiz.selectedPositions.forEach((key) => {
      f.push({ kind: 'method', position: key });
      f.push({ kind: 'type', position: key });
      f.push({ kind: 'configure', position: key });
    });
    return f;
  }
  function currentFlowStep() {
    return flow[stepIdx];
  }
  function updateWizTitle() {
    const el = $('rwb-wizTitle');
    if (!el) return;
    const step = currentFlowStep();
    if (!step || step.kind === 'position') {
      el.textContent = 'Add Your Branding';
      return;
    }
    const n = wiz.selectedPositions.length;
    const idx = wiz.selectedPositions.indexOf(step.position);
    // Resolved through getLabel() so the title reflects category-specific display names
    // (e.g. hoodiefz's "Lower Back" instead of the shared "Nape Of Neck" key — Round 15).
    const posConfig = positions.find((p) => p.key === step.position);
    const label = posConfig ? getLabel(posConfig, wiz.category) : step.position;
    el.textContent = n > 1 ? `Add Your Branding — ${label} (${idx + 1} Of ${n})` : 'Add Your Branding';
  }
  // Loads a given position's saved-so-far config into the live method/type/configure
  // UI — used both the first time we reach that position AND when navigating back to
  // one already partly (or fully) filled in, so nothing typed is lost either way.
  function loadPositionStepState(kind, key) {
    const sel = selections[key];
    if (!sel) return;
    if (kind === 'method') {
      wiz.method = sel.method;
      document.querySelectorAll('.rwb-method-card').forEach((el) => el.classList.toggle('rwb-selected', el.dataset.method === sel.method));
      updateMethodAvailability();
    } else if (kind === 'type') {
      wiz.type = sel.type;
      document.querySelectorAll('.rwb-type-card').forEach((el) => el.classList.toggle('rwb-selected', el.dataset.type === sel.type));
      $('rwb-configureLogo').classList.toggle('rwb-hidden', sel.type !== 'Logo');
      $('rwb-configureText').classList.toggle('rwb-hidden', sel.type !== 'Text');
    } else if (kind === 'configure') {
      wiz.type = sel.type;
      $('rwb-configureLogo').classList.toggle('rwb-hidden', sel.type !== 'Logo');
      $('rwb-configureText').classList.toggle('rwb-hidden', sel.type !== 'Text');
      wizText = { font: sel.font, colour: sel.colour };
      $('rwb-textLine1').value = sel.textLine1 || '';
      $('rwb-textLine2').value = sel.textLine2 || '';
      $('rwb-textLine3').value = sel.textLine3 || '';
      $('rwb-notesField').value = sel.notes || '';
      document.querySelectorAll('input[name=rwb-logoAlt]').forEach((r) => {
        r.checked =
          (r.id === 'rwb-noLogoRadio' && sel.logoAlt === 'no-logo') ||
          (r.id === 'rwb-haveLogoRadio' && sel.logoAlt === 'have-logo');
      });
      if (sel.uploadedFile) {
        renderFilePreview(sel.uploadedFile, sel.uploadedFilePreviewUrl);
      } else {
        resetDropzone();
      }
      renderFontList();
      renderColourList();
      updateTextPreview();
    }
  }
  // Reads whatever's live on screen back into the position's selection object — called
  // just before navigating away from a step (forward OR back), so nothing is lost.
  function captureCurrentStepState() {
    const step = currentFlowStep();
    if (!step || step.kind === 'position') return;
    const sel = selections[step.position];
    if (!sel) return;
    if (step.kind === 'method') {
      sel.method = wiz.method;
    } else if (step.kind === 'type') {
      sel.type = wiz.type;
    } else if (step.kind === 'configure') {
      sel.notes = $('rwb-notesField').value.trim();
      if (sel.type === 'Text') {
        sel.textLine1 = $('rwb-textLine1').value.trim();
        sel.textLine2 = $('rwb-textLine2').value.trim();
        sel.textLine3 = $('rwb-textLine3').value.trim();
        sel.font = wizText.font;
        sel.colour = wizText.colour;
      }
      // uploadedFile / logoAlt are written straight into `sel` by their own event
      // handlers as they happen (see handleFileChosen / the logoAlt radio listeners),
      // so there's nothing further to capture for them here.
    }
  }
  function enterStep() {
    const step = currentFlowStep();
    PHASES.forEach((s) => {
      $('rwb-step-' + s).classList.toggle('rwb-hidden', s !== step.kind);
    });
    $('rwb-backBtn').style.visibility = stepIdx === 0 ? 'hidden' : 'visible';
    const isLast = stepIdx === flow.length - 1;
    $('rwb-nextBtn').textContent = isLast ? (isSubmitting ? 'Adding…' : 'Add To Basket') : 'Continue';

    if (step.kind !== 'position') {
      wiz.position = step.position;
      loadPositionStepState(step.kind, step.position);
    }
    updateWizTitle();
    renderStepsBar();
    updateSidebar();
  }
  function setSubmittingButtonUI() {
    $('rwb-nextBtn').textContent = isSubmitting ? 'Adding…' : 'Add To Basket';
  }

  /* ---------------- method availability (some positions can't be embroidered) ---------------- */
  function updateMethodAvailability() {
    const posConfig = positions.find((p) => p.key === wiz.position);
    const blocked = !!(posConfig && posConfig.embroideryBlocked);
    const card = $('rwb-methodEmbroidery');
    const warn = $('rwb-methodEmbroideryWarn');
    card.classList.toggle('rwb-disabled', blocked);
    warn.classList.toggle('rwb-hidden', !blocked);
    if (blocked && wiz.method === 'Embroidery') {
      wiz.method = null;
      document.querySelectorAll('.rwb-method-card').forEach((el) => el.classList.remove('rwb-selected'));
      updateSidebar();
    }
  }

  /* ---------------- method / type steps ---------------- */
  function selectMethod(m) {
    wiz.method = m;
    document.querySelectorAll('.rwb-method-card').forEach((el) => el.classList.toggle('rwb-selected', el.dataset.method === m));
    updateSidebar();
  }
  function selectType(t) {
    wiz.type = t;
    document.querySelectorAll('.rwb-type-card').forEach((el) => el.classList.toggle('rwb-selected', el.dataset.type === t));
    $('rwb-configureLogo').classList.toggle('rwb-hidden', t !== 'Logo');
    $('rwb-configureText').classList.toggle('rwb-hidden', t !== 'Text');
    if (t === 'Text') { renderFontList(); renderColourList(); updateTextPreview(); }
    updateSidebar();
  }

  /* ---------------- logo upload ---------------- */
  function resetDropzone() {
    $('rwb-dzWrap').innerHTML = `<div class="rwb-dropzone" id="rwb-dzTrigger"><div class="rwb-dz-title">⬆️ Click To Upload Your Logo</div><div class="rwb-dz-sub">PNG, JPG Or PDF — Free Digitisation Included</div></div>`;
    $('rwb-dzTrigger').addEventListener('click', () => $('rwb-fileInput').click());
    $('rwb-fileInput').value = '';
  }
  function renderFilePreview(file, previewUrl) {
    $('rwb-dzWrap').innerHTML = `<div class="rwb-file-preview">
      <img src="${previewUrl || ''}" onerror="this.style.display='none'">
      <div><div class="rwb-fp-name">${escapeHtml(file.name)}</div><div style="font-size:11px;color:var(--rwb-grey-500);">Ready To Upload</div></div>
      <span class="rwb-fp-remove" id="rwb-fpRemove">Remove</span>
    </div>`;
    $('rwb-fpRemove').addEventListener('click', () => {
      const sel = selections[wiz.position];
      if (sel) {
        sel.uploadedFile = null;
        sel.uploadedFilePreviewUrl = '';
      }
      resetDropzone();
    });
  }
  function handleFileChosen(e) {
    const f = e.target.files[0];
    if (!f) return;
    const sel = selections[wiz.position];
    const reader = new FileReader();
    reader.onload = function (ev) {
      const previewUrl = f.type.startsWith('image') ? ev.target.result : '';
      if (sel) {
        sel.uploadedFile = f;
        sel.uploadedFilePreviewUrl = previewUrl;
      }
      renderFilePreview(f, previewUrl);
    };
    if (f.type.startsWith('image')) reader.readAsDataURL(f);
    else reader.onload({ target: { result: '' } });
  }
  function uploadToCloudinary(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    return fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`, { method: 'POST', body: formData })
      .then((r) => {
        if (!r.ok) throw new Error('Cloudinary upload failed (' + r.status + ')');
        return r.json();
      })
      .then((data) => data.secure_url);
  }

  /* ---------------- text customiser ---------------- */
  function renderFontList() {
    $('rwb-fontList').innerHTML = fonts
      .map(
        (f) => `<div class="rwb-font-row ${wizText.font.key === f.key ? 'rwb-selected' : ''}" style='font-family:${f.family};font-weight:${f.weight};letter-spacing:${f.letterSpacing};' data-key="${f.key}">${f.key}<span class="rwb-font-tick">✓</span></div>`
      )
      .join('');
    document.querySelectorAll('.rwb-font-row').forEach((el) => {
      el.addEventListener('click', () => {
        wizText.font = fonts.find((f) => f.key === el.dataset.key);
        renderFontList();
        updateTextPreview();
      });
    });
  }
  function renderColourList() {
    $('rwb-colourList').innerHTML = threadColours
      .map(
        (c) => `<div class="rwb-colour-dot ${wizText.colour.key === c.key ? 'rwb-selected' : ''}" style="background:${c.hex};" title="${c.key}" data-key="${c.key}"><span class="rwb-tick">✓</span></div>`
      )
      .join('');
    document.querySelectorAll('.rwb-colour-dot').forEach((el) => {
      el.addEventListener('click', () => {
        wizText.colour = threadColours.find((c) => c.key === el.dataset.key);
        renderColourList();
        updateTextPreview();
      });
    });
  }
  function updateTextPreview() {
    const box = $('rwb-textPreviewBox');
    if (!box) return;
    const l1 = $('rwb-textLine1').value.trim();
    const l2 = $('rwb-textLine2').value.trim();
    const l3 = $('rwb-textLine3').value.trim();
    box.style.background = '#1c2c48';
    if (!l1 && !l2 && !l3) {
      box.innerHTML = `<span class="rwb-ph">Your Text Will Preview Here</span>`;
      return;
    }
    const lineStyle = `font-family:${wizText.font.family};font-weight:${wizText.font.weight};letter-spacing:${wizText.font.letterSpacing};color:${wizText.colour.hex};`;
    box.innerHTML = [l1, l2, l3]
      .filter(Boolean)
      .map((l, i) => `<div style='${lineStyle}font-size:${i === 0 ? '19px' : '14px'};line-height:1.35;'>${escapeHtml(l)}</div>`)
      .join('');
  }

  /* ---------------- step navigation ---------------- */
  function renderStepsBar() {
    const labels = { position: 'Position', method: 'Method', type: 'Type', configure: 'Configure' };
    const currentKind = (currentFlowStep() || {}).kind || 'position';
    const currentPhaseIdx = PHASES.indexOf(currentKind);
    $('rwb-stepsBar').innerHTML = PHASES
      .map((s, i) => {
        const state = i < currentPhaseIdx ? 'rwb-done' : i === currentPhaseIdx ? 'rwb-active' : '';
        const sep = i < PHASES.length - 1 ? '<div class="rwb-step-sep"></div>' : '';
        return `<div class="rwb-step-chip ${state}"><span class="rwb-n">${i < currentPhaseIdx ? '✓' : i + 1}</span>${labels[s]}</div>${sep}`;
      })
      .join('');
  }
  function validateStep() {
    const step = currentFlowStep();
    if (step.kind === 'position' && wiz.selectedPositions.length === 0) {
      alert('Please Choose At Least One Position.');
      return false;
    }
    if (step.kind === 'method' && !wiz.method) { alert('Please Choose An Application Method.'); return false; }
    if (step.kind === 'type' && !wiz.type) { alert('Please Choose Logo Or Text.'); return false; }
    if (step.kind === 'configure' && wiz.type === 'Text' && !$('rwb-textLine1').value.trim()) {
      alert('Please Enter At Least A First Line Of Text.');
      return false;
    }
    return true;
  }
  function updateSidebar() {
    const n = (wiz.selectedPositions || []).length;
    const titleEl = $('rwb-sideTitle');
    if (titleEl) {
      titleEl.textContent = n > 1 ? `Customising 1 Product · ${n} Placements` : 'Customising 1 Product';
    }
    const list = $('rwb-selectionsList');
    if (list) {
      const step = currentFlowStep();
      list.innerHTML = (wiz.selectedPositions || [])
        .map((key, idx) => {
          const sel = selections[key] || {};
          const isFree = wiz.mode === 'included' && idx === 0;
          const pill = isFree
            ? `<span class="rwb-pill-included">Included</span>`
            : `<span class="rwb-pill-extra">+${EXTRA_BRANDING_PRICE_LABEL}</span>`;
          const methodTypeText = [sel.method, sel.type].filter(Boolean).join(' / ') || 'Not Configured Yet';
          const isActive = step && step.kind !== 'position' && key === step.position;
          // Resolved through getLabel() — see the Round 15 comment above `positions`.
          const posConfig = positions.find((p) => p.key === key);
          const label = posConfig ? getLabel(posConfig, wiz.category) : key;
          return `<div style="margin-bottom:10px;${isActive ? '' : 'opacity:.7;'}">
            <div style="display:flex;justify-content:space-between;align-items:center;font-size:12.5px;font-weight:700;color:var(--rwb-navy);"><span>${escapeHtml(label)}</span>${pill}</div>
            <div style="font-size:11px;color:var(--rwb-grey-500);margin-top:2px;">${escapeHtml(methodTypeText)}</div>
          </div>`;
        })
        .join('');
    }
    const totalEl = $('rwb-sideCostTotal');
    if (totalEl) {
      const extraCount = wiz.mode === 'included' ? Math.max(0, n - 1) : n;
      totalEl.innerHTML =
        extraCount > 0
          ? `<span class="rwb-pill-extra">${extraCount} × ${EXTRA_BRANDING_PRICE_LABEL}</span>`
          : `<span class="rwb-pill-included">Included In Price</span>`;
    }
  }
  function wizardNext() {
    if (isSubmitting) return;
    if (!validateStep()) return;
    captureCurrentStepState();
    if (stepIdx === 0) {
      // Leaving the Position step locks in this run's selections and builds the rest
      // of the flow (method/type/configure for each ticked position, in click order).
      flow = buildFlow();
    }
    if (stepIdx < flow.length - 1) {
      stepIdx++;
      enterStep();
    } else {
      finishWizard();
    }
  }
  function wizardBack() {
    if (stepIdx === 0) return;
    captureCurrentStepState();
    stepIdx--;
    enterStep();
  }

  /* ---------------- submit to the real Shopify cart ---------------- */
  function buildPropertiesFor(sel, uploadedUrl) {
    // 'Branding Position' is resolved through getLabel() (not the raw sel.position key) so
    // production/fulfillment sees the category-accurate placement name — e.g. hoodiefz
    // orders record "Lower Back", not "Nape Of Neck" (Round 15). occupiedPositionsFor() reads
    // this same property back later and is compared via getLabel() too (see
    // renderPositionGrid), so the two stay in sync for position-locking.
    const posConfig = positions.find((p) => p.key === sel.position);
    const positionLabel = posConfig ? getLabel(posConfig, wiz.category) : sel.position;
    const props = {
      'Branding Position': positionLabel,
      'Branding Method': sel.method,
      'Branding Type': sel.type,
    };
    if (sel.notes) props['Notes'] = sel.notes;
    if (sel.type === 'Logo') {
      props['Logo File'] = uploadedUrl || 'To Follow — Customer Will Email After Checkout';
    } else {
      props['Text Line 1'] = sel.textLine1;
      if (sel.textLine2) props['Text Line 2'] = sel.textLine2;
      if (sel.textLine3) props['Text Line 3'] = sel.textLine3;
      props['Font Style'] = sel.font.key;
      props['Thread/Print Colour'] = sel.colour.key;
    }
    return props;
  }
  function finishWizard() {
    if (isSubmitting) return;
    isSubmitting = true;
    captureCurrentStepState();
    setSubmittingButtonUI();

    // Ordered so the FIRST-ticked position (index 0) is the one treated as included —
    // matches the price badges shown on the Position step and the sidebar's tags.
    const orderedSelections = wiz.selectedPositions.map((key) => selections[key]);

    Promise.all(
      orderedSelections.map((sel) =>
        sel.type === 'Logo' && sel.uploadedFile ? uploadToCloudinary(sel.uploadedFile) : Promise.resolve(null)
      )
    )
      .then((urls) => {
        const propsList = orderedSelections.map((sel, i) => buildPropertiesFor(sel, urls[i]));
        // Only a run that started from a normal add-to-cart (mode 'included') has a free
        // slot at all — a run opened via "Add another logo/text" charges every position.
        const isIncludedFlow = wiz.mode === 'included';
        const freeProps = isIncludedFlow ? propsList[0] : null;
        const extraPropsList = isIncludedFlow ? propsList.slice(1) : propsList;

        const doChange = isIncludedFlow
          ? fetch(routeRoot + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              // IMPORTANT: quantity must be passed explicitly here. If omitted, Shopify's
              // cart/change.js collapses the line down to a quantity of 1 when properties
              // are changed at the same time — silently dropping the rest of that line's
              // units from the basket (e.g. a qty-5 line became qty-1 with no warning).
              body: JSON.stringify({ id: ctx.cartItemKey, quantity: ctx.quantity, properties: freeProps }),
            }).then((r) => {
              if (!r.ok) throw new Error('Cart update failed (' + r.status + ')');
              return r.json();
            })
          : Promise.resolve(null);

        return doChange.then((changeResp) => {
          if (extraPropsList.length === 0) return changeResp;
          // Every additional position (beyond the one free slot, if any) is billed as
          // its own extra-branding line — batched into ONE cart/add.js call (Shopify
          // accepts multiple `items`) rather than one request per position, so two or
          // more extra placements picked in the same pass land in the basket together.
          const items = extraPropsList.map((properties) => {
            properties['_Linked item key'] = ctx.cartItemKey;
            properties['For Item'] = ctx.productTitle + (ctx.variantTitle && ctx.variantTitle !== 'Default Title' ? ' — ' + ctx.variantTitle : '');
            return { id: EXTRA_BRANDING_VARIANT_ID, quantity: ctx.quantity, properties };
          });
          return fetch(routeRoot + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          }).then((r) => {
            if (!r.ok) throw new Error('Cart update failed (' + r.status + ')');
            return r.json();
          });
        });
      })
      .then((cartResponse) => {
        isSubmitting = false;
        closeWizard();
        document.dispatchEvent(new CustomEvent('rwb:cart-updated', { detail: cartResponse }));
      })
      .catch((err) => {
        isSubmitting = false;
        setSubmittingButtonUI();
        alert("Sorry, Something Went Wrong Saving Your Branding — Please Try Again. (" + err.message + ")");
      });
  }

  /* ---------------- wire static event listeners once ---------------- */
  function bindOnce() {
    $('rwb-toastCloseBtn').addEventListener('click', skipToast);
    $('rwb-toastContinueLink').addEventListener('click', skipToast);
    $('rwb-toastAddBtn').addEventListener('click', () => openWizard('included'));
    $('rwb-toastViewBasketBtn').addEventListener('click', () => { window.location.href = routeRoot + 'cart'; });

    $('rwb-wizCloseBtn').addEventListener('click', closeWizard);
    $('rwb-laterBtn').addEventListener('click', closeWizard);
    $('rwb-nextBtn').addEventListener('click', wizardNext);
    $('rwb-backBtn').addEventListener('click', wizardBack);
    $('rwb-methodEmbroidery').addEventListener('click', () => {
      if ($('rwb-methodEmbroidery').classList.contains('rwb-disabled')) return;
      selectMethod('Embroidery');
    });
    $('rwb-methodPrint').addEventListener('click', () => selectMethod('Print'));
    $('rwb-typeLogo').addEventListener('click', () => selectType('Logo'));
    $('rwb-typeText').addEventListener('click', () => selectType('Text'));
    $('rwb-fileInput').addEventListener('change', handleFileChosen);
    $('rwb-noLogoRadio').addEventListener('change', () => {
      const sel = selections[wiz.position];
      if (sel) sel.logoAlt = 'no-logo';
    });
    $('rwb-haveLogoRadio').addEventListener('change', () => {
      const sel = selections[wiz.position];
      if (sel) sel.logoAlt = 'have-logo';
    });
    ['rwb-textLine1', 'rwb-textLine2', 'rwb-textLine3'].forEach((id) => $(id).addEventListener('input', updateTextPreview));
    resetDropzone();
  }

  document.addEventListener('DOMContentLoaded', bindOnce);

  /* ---------------- public API ---------------- */
  window.RosewearBranding = {
    // Queues this cart item for a branding turn (toast, then optionally the full
    // wizard). If nothing is currently showing, it starts immediately; otherwise it
    // waits until the item(s) ahead of it are skipped or completed. This is what
    // lets a single bulk-order submission that creates several cart lines (e.g.
    // different sizes) give each line its own branding opportunity in turn.
    //
    // Trousers are excluded here (single choke point covering both the automatic
    // after-add-to-cart trigger below and the "Add Another Logo/Text" button handler
    // below, since both call this same open() function) — see isTrousersProduct().
    open: function (itemCtx) {
      if (itemCtx && isTrousersProduct(itemCtx.productTitle)) return;
      itemQueue.push(itemCtx);
      advanceQueue();
    },
  };

  /* ---------------- auto-trigger after a real add-to-cart ----------------
     Dawn's product-form.js (unchanged, no edits made to it) publishes this event
     after every successful /cart/add.js call: publish(PUB_SUB_EVENTS.cartUpdate,
     { source: 'product-form', productVariantId, cartData: response }). Subscribing
     here means the product page itself never has to change at all. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
      if (!data || data.source !== 'product-form') return;
      var cd = data.cartData;
      if (!cd || !cd.key) return; // unexpected/error response shape — bail safely, cart still works normally
      window.RosewearBranding.open({
        cartItemKey: cd.key,
        productTitle: cd.product_title,
        variantTitle: cd.variant_title,
        quantity: cd.quantity,
        imageUrl: cd.image,
        mode: 'included',
      });
    });
  }

  /* ---------------- "Add another logo/text to this item" (cart page/drawer) ----------------
     Delegated on document (not bound per-button) because Dawn re-renders the cart drawer/page
     markup via innerHTML replacement on every cart change — per-element listeners would be lost. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-rwb-add-another]');
    if (!btn) return;
    e.preventDefault();
    window.RosewearBranding.open({
      cartItemKey: btn.getAttribute('data-rwb-key'),
      productTitle: btn.getAttribute('data-rwb-title'),
      variantTitle: btn.getAttribute('data-rwb-variant'),
      quantity: parseInt(btn.getAttribute('data-rwb-qty'), 10) || 1,
      imageUrl: btn.getAttribute('data-rwb-image'),
      mode: 'extra',
    });
  });
})();
